import { lensApi } from "@/services/lensApi";
import { getSearchParamForRequest } from "@/utils/filters";

// --- Real list endpoint ---------------------------------------------------
//
// GET /api/lens/structures/{structureId}/properties returns every property
// type defined in the system, with each row carrying the current value (or
// null if not yet set on this structure) plus its calculation-config usages.
//
// Edit/history is not on the backend yet — the legacy fake update/counts
// implementation below is kept untouched so the existing edit modal keeps
// working as a UI fake.

export type StructurePropertyConfigCategory = "IAndI";

export interface StructurePropertyConfig {
	category: StructurePropertyConfigCategory;
	isRequired: boolean;
}

export interface StructurePropertyListItem {
	typeName: string;
	typeId: number;
	symbol: string | null;
	description: string | null;
	color: string | null;
	unit: string | null;
	minLimit: number | null;
	maxLimit: number | null;
	value: number | null;
	updatedDateTime: string | null;
	config: StructurePropertyConfig[];
}

export type StructurePropertyListResponse = Dryp.ApiList<StructurePropertyListItem>;

export interface StructurePropertyListFilters {
	search?: string;
	types?: string[];
	hasValue?: boolean;
	requiredForIAndI?: boolean;
	optionalForIAndI?: boolean;
}

const getListFilterParams = (filters?: StructurePropertyListFilters) => {
	const params: Record<string, unknown> = {
		...getSearchParamForRequest(filters?.search),
	};
	if (filters?.types && filters.types.length > 0) {
		params["filter[types]"] = filters.types;
	}
	if (filters?.hasValue !== undefined) {
		params["filter[hasValue]"] = filters.hasValue;
	}
	if (filters?.requiredForIAndI !== undefined) {
		params["filter[requiredForIAndI]"] = filters.requiredForIAndI;
	}
	if (filters?.optionalForIAndI !== undefined) {
		params["filter[optionalForIAndI]"] = filters.optionalForIAndI;
	}
	return params;
};

// --- Legacy fake (edit/history/counts) -----------------------------------
//
// Kept as-is until backend support lands. The list page no longer reads from
// this fake — it goes through the real endpoint above. The edit modal still
// consumes `StructureProperty` (with `history`) and `update` returns a fake.

export type StructurePropertyCategory = "connectivity" | "hydraulics" | "monitoring" | "general";

export type StructurePropertyHistoryRecord = {
	startsAt: string | null;
	endsAt: string | null;
	value: number;
};

export type StructureProperty = {
	id: string;
	structureId: Dryp.StructureId;
	typeId: string;
	name: string;
	category: StructurePropertyCategory;
	unit: string;
	currentValue: number | null;
	lastUpdatedAt: string;
	history: StructurePropertyHistoryRecord[];
};

export type StructurePropertyCounts = {
	configuredCount: number;
	missingForConnectivityCount: number;
};

export type StructurePropertyUpdatePayload = {
	history: StructurePropertyHistoryRecord[];
};

const FAKE_LATENCY_MS = 250;

const fakeFetch = <T>(value: T): Promise<T> =>
	new Promise(resolve => setTimeout(() => resolve(value), FAKE_LATENCY_MS));

const recomputeCurrentValue = (history: StructurePropertyHistoryRecord[]) => {
	const currentRecord = history.find(record => record.endsAt === null);
	return {
		currentValue: currentRecord ? currentRecord.value : null,
		lastUpdatedAt: currentRecord ? (currentRecord.startsAt ?? new Date().toISOString()) : new Date().toISOString(),
	};
};

export const structureProperty = {
	getForStructure: async (
		structureId: Dryp.StructureId,
		pagination: Dryp.PaginationQuery,
		sort?: Dryp.SortQuery,
		search?: string,
		filters?: Omit<StructurePropertyListFilters, "search">,
	): Promise<StructurePropertyListResponse> => {
		return lensApi.get<StructurePropertyListResponse>(`/structures/${structureId}/properties`, {
			params: {
				...pagination,
				...sort,
				...getListFilterParams({ ...filters, search }),
			},
		});
	},

	getCountsForStructure: async (structureId: Dryp.StructureId): Promise<StructurePropertyCounts> => {
		const configuredPromise = lensApi.get<StructurePropertyListResponse>(`/structures/${structureId}/properties`, {
			params: { pageNumber: 0, pageSize: 0, ...getListFilterParams({ hasValue: true, requiredForIAndI: true }) },
		});
		const missingForConnectivityPromise = lensApi.get<StructurePropertyListResponse>(
			`/structures/${structureId}/properties`,
			{
				params: {
					pageNumber: 0,
					pageSize: 0,
					...getListFilterParams({ hasValue: false, requiredForIAndI: true }),
				},
			},
		);
		const [configuredResponse, missingResponse] = await Promise.all([
			configuredPromise,
			missingForConnectivityPromise,
		]);
		return {
			configuredCount: configuredResponse.query?.pagination.totalItems ?? 0,
			missingForConnectivityCount: missingResponse.query?.pagination.totalItems ?? 0,
		};
	},

	// Fake. Persists nothing — kept so the existing edit modal continues to
	// behave reasonably until a real update endpoint lands.
	update: async (
		structureId: Dryp.StructureId,
		propertyId: string,
		payload: StructurePropertyUpdatePayload,
	): Promise<StructureProperty> => {
		const derivedFields = recomputeCurrentValue(payload.history);
		return fakeFetch({
			id: propertyId,
			structureId,
			typeId: propertyId,
			name: propertyId,
			category: "general",
			unit: "",
			...derivedFields,
			history: payload.history,
		});
	},
};
